import type { Player } from '../../types/player';
import type { LeagueConfig } from '../../types/league';

/**
 * Standard 1-QB League Formula
 * Traditional fantasy football with single QB starter
 */

export interface StandardFormulaWeights {
  previousPPG: number;
  projectedPoints: number;
  consistency: number;
  targetShare: number;
  redZoneTargets: number;
  strengthOfSchedule: number;
  injuryRisk: number;
  ageDecline: number;
  positionScarcity: number;
  teamOffense: number;
}

export const STANDARD_WEIGHTS: StandardFormulaWeights = {
  previousPPG: 0.25,      // Heavy weight on proven production
  projectedPoints: 0.20,  // Current year projections
  consistency: 0.15,      // Week-to-week reliability
  targetShare: 0.12,      // Share of team's targets/touches
  redZoneTargets: 0.08,   // TD opportunity
  strengthOfSchedule: 0.06, // Matchup difficulty
  injuryRisk: 0.05,       // Health concerns
  ageDecline: 0.04,       // Age-related decline
  positionScarcity: 0.03, // Less important in 1-QB
  teamOffense: 0.02       // Team context
};

export function calculateStandardValue(
  player: Player,
  leagueConfig: LeagueConfig
): number {
  const weights = STANDARD_WEIGHTS;
  
  // Base scoring factors
  const previousPPG = (player.lastYearPoints || 0) / 17;
  const projectedPoints = player.projection || player.projectedPoints || 0;
  const consistency = calculateConsistency(player);
  const targetShare = calculateTargetShare(player);
  const redZoneTargets = calculateRedZoneTargets(player);
  const strengthOfSchedule = calculateSOS(player);
  const injuryRisk = calculateInjuryRisk(player);
  const ageDecline = calculateAgeDecline(player);
  const positionScarcity = calculatePositionScarcity(player, leagueConfig, 'standard');
  const teamOffense = calculateTeamOffense(player);
  
  // Calculate weighted score
  const standardValue = 
    (weights.previousPPG * previousPPG) +
    (weights.projectedPoints * projectedPoints) +
    (weights.consistency * consistency) +
    (weights.targetShare * targetShare) +
    (weights.redZoneTargets * redZoneTargets) +
    (weights.strengthOfSchedule * strengthOfSchedule) +
    (weights.injuryRisk * injuryRisk) +
    (weights.ageDecline * ageDecline) +
    (weights.positionScarcity * positionScarcity) +
    (weights.teamOffense * teamOffense);
  
  // Apply WAR (Wins Above Replacement) for Standard format
  const warMultiplier = calculateStandardWAR(player, projectedPoints);
  
  // Apply QB saturation penalty for Standard (target: 1 QB in top 24)
  const qbSaturationPenalty = calculateStandardQBSaturation(player);
  
  // Apply TE tier penalties for Standard (target: exactly 3 TEs in top 24)
  const teTierPenalty = calculateStandardTETier(player);
  
  // Apply Kicker boost for Standard (target: 1 K in top 24)
  const kBoost = calculateStandardKickerBoost(player);
  
  return standardValue * warMultiplier * qbSaturationPenalty * teTierPenalty * kBoost;
}

// Helper functions
function calculateConsistency(player: Player): number {
  // Mock implementation - would use real game log data
  const baseConsistency = { QB: 0.8, RB: 0.6, WR: 0.5, TE: 0.7, K: 0.4, DEF: 0.5 };
  return baseConsistency[player.position as keyof typeof baseConsistency] || 0.5;
}

function calculateTargetShare(player: Player): number {
  const baseShares = { QB: 1.0, RB: 0.15, WR: 0.20, TE: 0.15, K: 0, DEF: 0 };
  return baseShares[player.position as keyof typeof baseShares] || 0;
}

function calculateRedZoneTargets(player: Player): number {
  const baseRZ = { QB: 0.8, RB: 0.6, WR: 0.4, TE: 0.5, K: 0.3, DEF: 0 };
  return baseRZ[player.position as keyof typeof baseRZ] || 0;
}

function calculateSOS(player: Player): number {
  // Mock - would use real schedule strength data
  return 0.5 + (Math.random() - 0.5) * 0.2; // Random between 0.4-0.6
}

function calculateInjuryRisk(player: Player): number {
  const positionRisk = { QB: 0.9, RB: 0.7, WR: 0.8, TE: 0.8, K: 0.95, DEF: 0.9 };
  const ageRisk = (player.age || 26) > 30 ? 0.85 : 1.0;
  return (positionRisk[player.position as keyof typeof positionRisk] || 0.8) * ageRisk;
}

function calculateAgeDecline(player: Player): number {
  const age = player.age || 26;
  if (age < 24) return 1.1;  // Youth bonus
  if (age < 27) return 1.0;  // Prime
  if (age < 30) return 0.95; // Slight decline
  if (age < 33) return 0.85; // Noticeable decline
  return 0.7; // Significant decline
}

function calculatePositionScarcity(player: Player, leagueConfig: LeagueConfig, format: string): number {
  // Standard league scarcity - MUCH higher RB/TE premium
  const scarcity = {
    QB: 0.6,  // REDUCED - no premium in 1-QB
    RB: 1.8,  // MUCH HIGHER - scarcity is king in standard
    WR: 1.2,  // Moderate scarcity  
    TE: 2.0,  // MUCH HIGHER - elite TEs are game-changers
    K: 0.7,   // Low scarcity
    DEF: 0.7  // Low scarcity
  };
  
  return scarcity[player.position as keyof typeof scarcity] || 1.0;
}

function calculateTeamOffense(player: Player): number {
  // Mock team offensive strength
  const strongOffenses = ['BUF', 'KC', 'SF', 'DAL', 'MIA', 'CIN'];
  return strongOffenses.includes(player.team) ? 1.2 : 1.0;
}

export function getStandardPositionPriority(): string[] {
  return ['RB', 'WR', 'RB', 'WR', 'TE', 'QB', 'RB', 'WR'];
}

export function getStandardDraftStrategy(): string[] {
  return [
    'RB-heavy early rounds strategy viable',
    'Elite TE provides significant advantage over replacement',
    'Wait on QB until rounds 6-10 - many viable options',
    'Stream defenses based on matchups',
    'Kicker is typically last pick',
    'Target high-floor players for consistency',
    'Handcuff valuable RBs in later rounds'
  ];
}

function calculateStandardWAR(player: Player, projectedPoints: number): number {
  const position = player.position;
  
  // Standard format replacement levels (12-team league)
  const replacementPoints = {
    QB: 280,  // QB12 level - many viable options
    RB: 140,  // RB24 level - scarcity kicks in hard  
    WR: 130,  // WR36 level - depth available
    TE: 80,   // TE12 level - BIGGER drop after elite tier
    K: 85,    // K12 level - BOOST to get 1 in top 24
    DEF: 80   // DEF12 level
  };
  
  const replacementLevel = replacementPoints[position as keyof typeof replacementPoints] || 100;
  const pointsAboveReplacement = Math.max(0, projectedPoints - replacementLevel);
  
  // Convert to WAR multiplier with position-specific scaling
  const positionMultipliers = {
    QB: 0.5,   // Low impact in 1-QB
    RB: 1.5,   // High impact due to scarcity
    WR: 1.0,   // Standard impact
    TE: 2.5,   // VERY high impact for elite tier - boost Andrews/Hockenson
    K: 0.8,    // BOOST to get 1 in top 24
    DEF: 0.3   // Low impact
  };
  
  const posMultiplier = positionMultipliers[position as keyof typeof positionMultipliers] || 1.0;
  const warMultiplier = 1.0 + (pointsAboveReplacement * posMultiplier / 150);
  
  return Math.min(3.0, warMultiplier); // Cap at 3x
}

function calculateStandardQBSaturation(player: Player): number {
  if (player.position !== 'QB') return 1.0;
  
  // Standard format should have EXACTLY 1 QB in top 24
  // EXTREME penalties - only Josh Allen should make top 24
  const qbRank = getQBTier(player);
  
  if (qbRank <= 1 && player.name.includes('Allen')) return 0.9;  // Only Josh Allen
  if (qbRank <= 1) return 0.15;     // Other elite QBs - 85% penalty
  if (qbRank <= 3) return 0.05;     // QB2-3 - 95% penalty
  return 0.01;                      // QB4+ - 99% penalty
}

function calculateStandardTETier(player: Player): number {
  if (player.position !== 'TE') return 1.0;
  
  // Standard format should have EXACTLY 3 TEs in top 24: Kelce, LaPorta, Kittle
  const eliteTEs = ['Kelce', 'LaPorta', 'Kittle'];  // Only these 3 in top 24
  const goodTEs = ['Andrews', 'Hockenson', 'McBride']; // Push to top 30-40
  const midTEs = ['Kincaid', 'Pitts', 'Njoku'];      // Push to 40-60 range
  
  // Elite tier - top 24
  if (eliteTEs.some(name => player.name.includes(name))) return 1.0;
  
  // Good tier - should be 30-40 range  
  if (goodTEs.some(name => player.name.includes(name))) return 0.7;
  
  // Mid tier - should be 40-60 range
  if (midTEs.some(name => player.name.includes(name))) return 0.5;
  
  // Everyone else - massive penalty (Ian Thomas, etc.)
  return 0.1;  // 90% penalty for non-elite TEs
}

function calculateStandardKickerBoost(player: Player): number {
  if (player.position !== 'K') return 1.0;
  
  // Boost elite kickers to get 1 in top 24
  const eliteKickers = ['Tucker', 'Elliott', 'Bass']; // Top kickers
  
  if (eliteKickers.some(name => player.name.includes(name))) {
    return 2.0; // 100% boost for elite kickers
  }
  
  return 1.0; // No boost for other kickers
}

function getQBTier(player: Player): number {
  // Mock QB ranking - would use real consensus rankings  
  const tierOneQBs = ['Josh Allen', 'Lamar Jackson', 'Jalen Hurts'];
  const tierTwoQBs = ['Joe Burrow', 'Jayden Daniels', 'Dak Prescott', 'Tua Tagovailoa', 'Brock Purdy', 'CJ Stroud'];
  const tierThreeQBs = ['Kyler Murray', 'Aaron Rodgers', 'Trevor Lawrence', 'Jordan Love', 'Caleb Williams', 'Anthony Richardson'];
  
  if (tierOneQBs.some(name => player.name.includes(name.split(' ')[1]))) return 1;
  if (tierTwoQBs.some(name => player.name.includes(name.split(' ')[1]))) return 4;  
  if (tierThreeQBs.some(name => player.name.includes(name.split(' ')[1]))) return 8;
  
  return 15; // Default lower tier
}